import { useState } from 'react';

import { SelectComponents, Select } from '@frontend/legacy-components';

import SingleDateInput from 'components/listTablePage/searchAndFilters/components/filters/components/DateRelationFilter/SingleDateInput';

import { isEmptyString } from 'utils/stringUtils';

import type {
  ExcludableDateRelation,
  SingleDateRelationFilterValue,
  SingleDateValue,
} from './types';

type RelativeOrSingleDatePickerProps<
  ExcludedRelations extends ExcludableDateRelation,
> = {
  blockFutureSelections: boolean;
  relationFilterValue: SingleDateRelationFilterValue<ExcludedRelations>;
  onRelativeDateChange: (relativeDate: string) => void;
  onStartDateChange: (selectedDate: SingleDateValue) => void;
  dateTimeFormat: string;
  showTimeSettings: object | boolean;
  isPopoverOverlayEnabled?: boolean;
};

type RelativeDateOption = {
  value: string;
  label: string;
  futureDate: boolean;
};

export const relativeDateOptions: RelativeDateOption[] = [
  { value: '', label: 'a specific date', futureDate: false },
  { value: 'now', label: 'now', futureDate: true },
  { value: 'today', label: 'today', futureDate: true },
  { value: 'tomorrow', label: 'tomorrow', futureDate: true },
  { value: 'yesterday', label: 'yesterday', futureDate: false },
  { value: 'this_week', label: 'this week', futureDate: true },
  { value: 'next_week', label: 'next week', futureDate: true },
  { value: 'last_week', label: 'last week', futureDate: false },
  { value: 'this_month', label: 'this month', futureDate: true },
  { value: 'next_month', label: 'next month', futureDate: true },
  { value: 'last_month', label: 'last month', futureDate: false },
];

const SelectContainer = ({ innerProps, ...restProps }) => {
  return (
    <SelectComponents.SelectContainer
      innerProps={{ flexBasis: '160px', flexShrink: 0, ...innerProps }}
      {...restProps}
    />
  );
};

const SelectControl = (props) => (
  <SelectComponents.Control
    {...props}
    innerProps={{ ...props.innerProps, borderLeftRadius: 0 }}
  />
);

const ShowSpecificDateControl = (props) => (
  <SelectComponents.Control
    {...props}
    innerProps={{ ...props.innerProps, borderRadius: 0, borderRight: 0 }}
  />
);

const RelativeOrSingleDatePicker = <
  ExcludedRelations extends ExcludableDateRelation,
>({
  blockFutureSelections,
  relationFilterValue,
  onRelativeDateChange,
  onStartDateChange,
  dateTimeFormat,
  showTimeSettings,
  isPopoverOverlayEnabled = false,
}: RelativeOrSingleDatePickerProps<ExcludedRelations>) => {
  const [showSpecificDate, setShowSpecificDate] = useState(
    relationFilterValue.relativeDate === '',
  );

  return (
    <>
      <Select
        isPopoverOverlayEnabled={isPopoverOverlayEnabled}
        components={{
          SelectContainer,
          Control: showSpecificDate ? ShowSpecificDateControl : SelectControl,
        }}
        value={relativeDateOptions.find(
          (option) => option.value === relationFilterValue.relativeDate,
        )}
        options={
          blockFutureSelections
            ? relativeDateOptions.filter((option) => !option.futureDate)
            : relativeDateOptions
        }
        isClearable={false}
        onChange={(selectedOption: RelativeDateOption) => {
          const { value: relativeDate } = selectedOption;
          setShowSpecificDate(isEmptyString(relativeDate));
          onRelativeDateChange(relativeDate);
        }}
      />
      {showSpecificDate && (
        <SingleDateInput
          relationFilterValue={relationFilterValue}
          onChange={onStartDateChange}
          dateTimeFormat={dateTimeFormat}
          showTimeSettings={showTimeSettings}
        />
      )}
    </>
  );
};

export default RelativeOrSingleDatePicker;
